import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaSection from "@/components/home/CtaSection";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/content";

const CHAPTERS = [
  {
    num: "01", title: "Discover",
    text: "Understand the business, users, challenges and objectives.",
    detail: "Every engagement starts with listening. We map your workflows, interview stakeholders and identify what success actually looks like — before a single line of code is written.",
    deliverables: ["Stakeholder interviews", "Workflow mapping", "Success criteria", "Technical constraints review"],
  },
  {
    num: "02", title: "Design",
    text: "Define workflows, user experience, architecture and solution requirements.",
    detail: "We translate discovery into a concrete blueprint: user journeys, interface designs, system architecture and a scoped, prioritized build plan.",
    deliverables: ["UX flows & wireframes", "System architecture", "Data model", "Scoped build plan"],
  },
  {
    num: "03", title: "Build",
    text: "Develop the platform using modern, maintainable technologies.",
    detail: "Iterative development with regular demos. You see progress as it happens, and feedback shapes the product while it's still flexible.",
    deliverables: ["Iterative releases", "Regular demos", "Code review", "Documentation"],
  },
  {
    num: "04", title: "Test",
    text: "Perform functional, security, usability and performance testing.",
    detail: "Every release is verified against the workflows it supports — functional behavior, security posture, usability and performance under real conditions.",
    deliverables: ["Functional testing", "Security review", "Usability checks", "Performance validation"],
  },
  {
    num: "05", title: "Launch",
    text: "Deploy the solution into production.",
    detail: "A controlled rollout with monitoring, backups and rollback plans — launch day as a milestone, not a gamble.",
    deliverables: ["Production deployment", "Monitoring & alerts", "Backup strategy", "Rollback plan"],
  },
  {
    num: "06", title: "Improve",
    text: "Continue supporting, monitoring and expanding the software.",
    detail: "Launch is the beginning. We stay involved — supporting, monitoring and expanding the platform as your business evolves.",
    deliverables: ["Ongoing support", "Usage insights", "Iteration roadmap", "Continuous improvement"],
  },
];

export default function Process() {
  const { data } = useSite();
  const seo = data?.settings?.seo?.["/our-process"];

  return (
    <>
      <Seo
        title={seo?.title || "Our Development Process | LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "Discover, Design, Build, Test, Launch, Improve — the six-step LogiTech Nexus development process for reliable software delivery."}
      />
      <PageHero
        eyebrow="Our Process"
        title="Six Chapters."
        accent="One Reliable Path to Launch."
        subtitle="A clear, transparent process from first conversation to long-term partnership."
        testid="process-hero"
      />

      <section className="bg-void pb-28" data-testid="process-chapters">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.num} delay={0.05} y={30}>
              <div
                className={`group relative grid gap-8 border-l border-slate-800 py-12 pl-10 sm:pl-16 lg:grid-cols-[auto_1fr_1fr] ${
                  i === CHAPTERS.length - 1 ? "" : "border-b-0"
                }`}
                data-testid={`chapter-${ch.num}`}
              >
                <span className="absolute -left-[6px] top-14 h-3 w-3 rounded-full bg-blue-500 shadow-[0_0_14px_rgba(59,130,246,0.9)]" />
                <p className="font-mono text-6xl font-semibold leading-none text-slate-800 transition-colors duration-500 group-hover:text-blue-500/50 sm:text-7xl">
                  {ch.num}
                </p>
                <div>
                  <h2 className="font-display text-2xl font-semibold text-white sm:text-3xl">{ch.title}</h2>
                  <p className="mt-2 font-display text-base text-blue-300/90">{ch.text}</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">{ch.detail}</p>
                </div>
                <div className="rounded-xl border border-slate-800/70 bg-navy/50 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400">Deliverables</p>
                  <ul className="mt-4 space-y-2.5">
                    {ch.deliverables.map((d) => (
                      <li key={d} className="flex items-center gap-2.5 text-sm text-slate-300">
                        <span className="h-1 w-1 rounded-full bg-cyan-400" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaSection />
    </>
  );
}
